/**
 * Verificação On-Chain de Proof of Life
 * Busca transação na Polygon e decodifica evento LifeProven
 */
import { ethers } from 'ethers';
import ProofOfLifeABI from '../proof-of-life.json';

const POLYGON_RPC = 'https://polygon-rpc.com';
const CONTRACT_ADDRESS = '0x5d66bec24562493c5a640854e57c535082ee6b50';

/**
 * Busca e decodifica evento LifeProven de uma transação
 * @param {string} txHash - Hash da transação
 * @returns {Promise<{user: string, proofHash: string, reward: string, blockNumber: number, timestamp: number}>}
 */
export const verifyProofOnChain = async (txHash) => {
  try {
    // Conectar ao provider Polygon (sem necessidade de wallet)
    const provider = new ethers.JsonRpcProvider(POLYGON_RPC);

    // Buscar receipt da transação
    const receipt = await provider.getTransactionReceipt(txHash);

    if (!receipt) {
      throw new Error('Transação não encontrada na Polygon');
    }

    if (receipt.status !== 1) {
      throw new Error('Transação falhou na blockchain');
    }

    // Criar interface do contrato para decodificar eventos
    const contractInterface = new ethers.Interface(ProofOfLifeABI);

    // Procurar evento LifeProven nos logs
    let lifeProvenEvent = null;
    for (const log of receipt.logs) {
      try {
        // Verificar se o log é do nosso contrato
        if (log.address.toLowerCase() !== CONTRACT_ADDRESS.toLowerCase()) {
          continue;
        }

        // Tentar decodificar como LifeProven
        // Verificar se o primeiro topic corresponde ao evento
        const eventSignature = ethers.id('LifeProven(address,bytes32,uint256)');
        if (log.topics[0] === eventSignature) {
          const parsed = contractInterface.parseLog({
            topics: log.topics,
            data: log.data
          });

          if (parsed && parsed.name === 'LifeProven') {
            lifeProvenEvent = {
              user: parsed.args.user,
              proofHash: parsed.args.proofHash,
              reward: parsed.args.reward.toString(),
              blockNumber: receipt.blockNumber,
              timestamp: null // Será preenchido depois
            };
            break;
          }
        }
      } catch (e) {
        // Log não é do nosso evento, continuar
        continue;
      }
    }

    if (!lifeProvenEvent) {
      throw new Error('Evento LifeProven não encontrado na transação');
    }

    // Buscar timestamp do bloco
    try {
      const block = await provider.getBlock(receipt.blockNumber);
      lifeProvenEvent.timestamp = block.timestamp;
    } catch (e) {
      console.warn('Não foi possível obter timestamp do bloco:', e);
    }

    return {
      success: true,
      txHash,
      ...lifeProvenEvent,
      contractAddress: CONTRACT_ADDRESS,
      explorerUrl: `https://polygonscan.com/tx/${txHash}`
    };

  } catch (error) {
    console.error('Erro ao verificar proof on-chain:', error);
    return {
      success: false,
      error: error.message,
      txHash
    };
  }
};

/**
 * Verifica se uma transação existe e é válida
 * @param {string} txHash - Hash da transação
 * @returns {Promise<boolean>}
 */
export const transactionExists = async (txHash) => {
  try {
    const provider = new ethers.JsonRpcProvider(POLYGON_RPC);
    const receipt = await provider.getTransactionReceipt(txHash);
    return receipt !== null && receipt.status === 1;
  } catch (error) {
    return false;
  }
};

/**
 * Busca todas as proofs de um endereço (via eventos)
 * @param {string} userAddress - Endereço do usuário
 * @param {number} fromBlock - Bloco inicial (opcional)
 * @returns {Promise<Array>}
 */
export const getUserProofs = async (userAddress, fromBlock = 0) => {
  try {
    const provider = new ethers.JsonRpcProvider(POLYGON_RPC);
    const contractInterface = new ethers.Interface(ProofOfLifeABI);

    // Criar filtro para eventos LifeProven do usuário
    // LifeProven(address indexed user, bytes32 proofHash, uint256 reward)
    const eventSignature = ethers.id('LifeProven(address,bytes32,uint256)');
    const userTopic = ethers.zeroPadValue(userAddress, 32);

    const filter = {
      address: CONTRACT_ADDRESS,
      topics: [
        eventSignature,
        userTopic // user (indexed)
      ],
      fromBlock: fromBlock || 0,
      toBlock: 'latest'
    };

    const logs = await provider.getLogs(filter);

    return logs.map(log => {
      const parsed = contractInterface.parseLog({
        topics: log.topics,
        data: log.data
      });

      return {
        user: parsed.args.user,
        proofHash: parsed.args.proofHash,
        reward: parsed.args.reward.toString(),
        blockNumber: log.blockNumber,
        txHash: log.transactionHash
      };
    });

  } catch (error) {
    console.error('Erro ao buscar proofs do usuário:', error);
    return [];
  }
};
